import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import Hoot from "./Hoot";
import { Avatar, Tooltip, Switch, FormControlLabel } from "@mui/material";
import { toast } from "react-toastify";
import {
  FiTarget, FiX, FiPlus, FiEdit2, FiTrash2,
  FiSave, FiEye, FiEyeOff, FiRefreshCw,
  FiCalendar, FiUsers, FiAward, FiTrendingUp,
  FiMaximize, FiMinimize, FiMonitor,
} from "react-icons/fi";

const PHRASES_SHOW_K = "educrm_idle_phrases_enabled";

const PALETTE = [
  "#6052ff","#10b981","#f59e0b","#ef4444","#8b5cf6",
  "#06b6d4","#f97316","#ec4899","#14b8a6","#84cc16",
];

const IDLE_MS        = 2 * 60 * 1000;
const IDLE_ROTATE_MS = 30 * 1000;
const IDLE_QUOTES_RAW = [
  // Steve Jobs (5)
  { text:"O seu tempo é limitado. Não o desperdice a viver a vida de outra pessoa.", author:"Steve Jobs", role:"Co-fundador da Apple", gradient:"linear-gradient(135deg,#16a34a,#166534)", accent:"#bbf7d0" },
  { text:"A inovação distingue um líder de um seguidor.", author:"Steve Jobs", role:"Co-fundador da Apple", gradient:"linear-gradient(135deg,#0f766e,#065f46)", accent:"#5eead4" },
  { text:"A única forma de fazer um trabalho excelente é amar o que se faz.", author:"Steve Jobs", role:"Co-fundador da Apple", gradient:"linear-gradient(135deg,#065f46,#14532d)", accent:"#34d399" },
  { text:"Não deixes que o barulho das opiniões dos outros abafe a tua própria voz interior.", author:"Steve Jobs", role:"Co-fundador da Apple", gradient:"linear-gradient(135deg,#134e4a,#0f766e)", accent:"#99f6e4" },
  { text:"Não podes ligar os pontos olhando para a frente. Só consegues ligá-los olhando para trás.", author:"Steve Jobs", role:"Co-fundador da Apple", gradient:"linear-gradient(135deg,#166534,#14532d)", accent:"#a3e635" },
  // Warren Buffett (2)
  { text:"Regra n.º 1: Nunca perca dinheiro. Regra n.º 2: Nunca se esqueça da regra n.º 1.", author:"Warren Buffett", role:"CEO da Berkshire Hathaway", gradient:"linear-gradient(135deg,#1d4ed8,#1e3a8a)", accent:"#93c5fd" },
  { text:"Rodeia-te dos melhores e o teu resultado será o melhor.", author:"Warren Buffett", role:"CEO da Berkshire Hathaway", gradient:"linear-gradient(135deg,#14532d,#166534)", accent:"#bbf7d0" },
  // David Goggins (8)
  { text:"A tua mente desiste muito antes do teu corpo. Quando sentires que chegaste ao limite, só atingiste 40% do teu potencial.", author:"David Goggins", role:"Navy SEAL & Ultramaratonista", gradient:"linear-gradient(135deg,#0f0f0f,#1c0a00)", accent:"#f97316" },
  { text:"Ninguém vai aparecer a salvar-te. Tens de ir buscar o que queres tu mesmo.", author:"David Goggins", role:"Navy SEAL & Ultramaratonista", gradient:"linear-gradient(135deg,#18181b,#0c0c0c)", accent:"#ef4444" },
  { text:"O desconforto é onde o crescimento acontece. Se estás confortável, estás parado.", author:"David Goggins", role:"Navy SEAL & Ultramaratonista", gradient:"linear-gradient(135deg,#1c0a00,#0f0f0f)", accent:"#fb923c" },
  { text:"A mediocridade é como uma droga suave. Confortável, indolor — e lentamente mata tudo o que podias ter sido.", author:"David Goggins", role:"Navy SEAL & Ultramaratonista", gradient:"linear-gradient(135deg,#0f172a,#1a0000)", accent:"#e85d04" },
  { text:"Quando a tua mente diz para parar, pergunta-lhe: porque? Ela raramente tem uma boa resposta.", author:"David Goggins", role:"Navy SEAL & Ultramaratonista", gradient:"linear-gradient(135deg,#1a0000,#150a00)", accent:"#fca5a5" },
  { text:"A maioria das pessoas acorda e espera que o dia seja bom. Os vencedores acordam e constroem um dia bom.", author:"David Goggins", role:"Navy SEAL & Ultramaratonista", gradient:"linear-gradient(135deg,#0a0a0a,#0c0c0c)", accent:"#fdba74" },
  { text:"Para cada dia que passas a fazer o mínimo, os outros estão a construir a versão deles que tu nunca alcançarás.", author:"David Goggins", role:"Navy SEAL & Ultramaratonista", gradient:"linear-gradient(135deg,#0c0c0c,#150a00)", accent:"#fbbf24" },
  { text:"Endurece a tua mente da mesma forma que endureces as tuas mãos. A mente endurece com o uso, não com o descanso.", author:"David Goggins", role:"Navy SEAL & Ultramaratonista", gradient:"linear-gradient(135deg,#150a00,#1c0a00)", accent:"#f97316" },
  // Andrew Tate (7)
  { text:"A pobreza é uma doença transmitida de geração em geração por pessoas que nunca decidiram ser mais.", author:"Andrew Tate", role:"Empreendedor & Campeão de Kickboxing", gradient:"linear-gradient(135deg,#1a0a2e,#0d0d1a)", accent:"#c084fc" },
  { text:"Tens de decidir: vais viver a tua vida segundo os teus termos ou vais viver a vida de outra pessoa segundo os termos dela?", author:"Andrew Tate", role:"Empreendedor & Campeão de Kickboxing", gradient:"linear-gradient(135deg,#0d0d1a,#0f0a1e)", accent:"#818cf8" },
  { text:"Ninguém de sucesso ficou em casa a ver televisão enquanto os seus sonhos ardiam. Levanta-te.", author:"Andrew Tate", role:"Empreendedor & Campeão de Kickboxing", gradient:"linear-gradient(135deg,#120820,#160028)", accent:"#a78bfa" },
  { text:"O mundo recompensa os que se responsabilizam por tudo — incluindo as coisas que não são culpa deles.", author:"Andrew Tate", role:"Empreendedor & Campeão de Kickboxing", gradient:"linear-gradient(135deg,#0f0a1e,#1a0033)", accent:"#c4b5fd" },
  { text:"A velocidade de execução separa os que chegam dos que ficam a planear para sempre.", author:"Andrew Tate", role:"Empreendedor & Campeão de Kickboxing", gradient:"linear-gradient(135deg,#1a0033,#160028)", accent:"#d946ef" },
  { text:"O homem fraco diz não tive oportunidades. O homem forte cria as suas oportunidades com as mãos que tem.", author:"Andrew Tate", role:"Empreendedor & Campeão de Kickboxing", gradient:"linear-gradient(135deg,#0e0e1f,#0d0d1a)", accent:"#7c3aed" },
  { text:"Toda a gente quer o prémio. Poucos querem o processo. É exatamente por isso que os prémios são escassos.", author:"Andrew Tate", role:"Empreendedor & Campeão de Kickboxing", gradient:"linear-gradient(135deg,#160028,#1a0a2e)", accent:"#9333ea" },
  // Outros
  { text:"Não te preocupes com o fracasso. Preocupa-te com as hipóteses que perdes quando não tentas.", author:"Jack Ma", role:"Fundador do Alibaba", gradient:"linear-gradient(135deg,#b45309,#92400e)", accent:"#fcd34d" },
  { text:"Se tens uma ideia mas não actuas sobre ela, essa ideia nunca te tornará melhor.", author:"Jack Ma", role:"Fundador do Alibaba", gradient:"linear-gradient(135deg,#7f1d1d,#991b1b)", accent:"#fca5a5" },
  { text:"O sucesso não é definitivo. O fracasso não é fatal. O que conta é a coragem de continuar.", author:"Winston Churchill", role:"Ex-Primeiro Ministro britânico", gradient:"linear-gradient(135deg,#1e3a5f,#1e40af)", accent:"#60a5fa" },
  { text:"O sucesso é ir de fracasso em fracasso sem perder o entusiasmo.", author:"Winston Churchill", role:"Ex-Primeiro Ministro britânico", gradient:"linear-gradient(135deg,#312e81,#1e40af)", accent:"#c4b5fd" },
  { text:"Não estamos a tentar ser a empresa maior. Estamos a tentar ser a melhor.", author:"Jeff Bezos", role:"Fundador da Amazon", gradient:"linear-gradient(135deg,#1c1c2e,#0f172a)", accent:"#a78bfa" },
  { text:"Sonha em grande. Começa pequeno. Age agora.", author:"Robin Sharma", role:"Autor de O Monge que Vendeu o Ferrari", gradient:"linear-gradient(135deg,#dc2626,#b91c1c)", accent:"#fca5a5" },
  { text:"O verdadeiro poder não vem do ter, mas do dar.", author:"Elon Musk", role:"CEO da Tesla & SpaceX", gradient:"linear-gradient(135deg,#1a1a2e,#0f172a)", accent:"#818cf8" },
  { text:"Seja a mudança que deseja ver no mundo.", author:"Mahatma Gandhi", role:"Líder independentista indiano", gradient:"linear-gradient(135deg,#166534,#14532d)", accent:"#bbf7d0" },
  { text:"Primeiro eles ignoram-te, depois riem de ti, depois combatem-te, depois tu vences.", author:"Mahatma Gandhi", role:"Líder independentista indiano", gradient:"linear-gradient(135deg,#78350f,#92400e)", accent:"#fde68a" },
  { text:"A melhor maneira de prever o futuro é criá-lo.", author:"Peter Drucker", role:"Pai da gestão moderna", gradient:"linear-gradient(135deg,#1d4ed8,#1e40af)", accent:"#bfdbfe" },
  { text:"A melhor forma de começar é parar de falar e começar a fazer.", author:"Walt Disney", role:"Fundador da Walt Disney Company", gradient:"linear-gradient(135deg,#172554,#1e3a8a)", accent:"#93c5fd" },
  { text:"O segredo de avançar é começar.", author:"Mark Twain", role:"Escritor e humorista americano", gradient:"linear-gradient(135deg,#0c4a6e,#075985)", accent:"#7dd3fc" },
  { text:"Nunca confundas movimento com acção.", author:"Ernest Hemingway", role:"Escritor Nobel da Literatura", gradient:"linear-gradient(135deg,#7c3aed,#6d28d9)", accent:"#ddd6fe" },
  { text:"Foca-te em ser produtivo em vez de estar ocupado.", author:"Tim Ferriss", role:"Autor de A Semana de 4 Horas", gradient:"linear-gradient(135deg,#065f46,#047857)", accent:"#6ee7b7" },
  { text:"Não conta quantas vezes caíste. Conta quantas vezes te levantaste.", author:"Nelson Mandela", role:"Ex-Presidente da África do Sul", gradient:"linear-gradient(135deg,#7c2d12,#991b1b)", accent:"#fed7aa" },
  { text:"Podes falhar em muitas coisas, mas enquanto não desistires, nunca és um fracassado.", author:"Albert Einstein", role:"Físico e Nobel da Física", gradient:"linear-gradient(135deg,#1a3a2a,#14532d)", accent:"#4ade80" },
  { text:"Sê corajoso. Toma riscos. Nada pode substituir a experiência.", author:"Paulo Coelho", role:"Escritor de O Alquimista", gradient:"linear-gradient(135deg,#134e4a,#0f766e)", accent:"#5eead4" },
  { text:"A motivação faz-te começar. O hábito faz-te continuar.", author:"Jim Ryun", role:"Atleta olímpico americano", gradient:"linear-gradient(135deg,#1c1c2e,#1e1b4b)", accent:"#818cf8" },
  { text:"O fracasso é simplesmente a oportunidade de começar de novo, desta vez mais inteligente.", author:"Henry Ford", role:"Fundador da Ford Motor Company", gradient:"linear-gradient(135deg,#3f3f46,#27272a)", accent:"#e4e4e7" },
  { text:"Não temos de ser grandes para começar, mas temos de começar para ser grandes.", author:"Zig Ziglar", role:"Escritor & orador motivacional", gradient:"linear-gradient(135deg,#14532d,#166534)", accent:"#86efac" },
];

function interleaveByAuthor(arr) {
  const map = {};
  arr.forEach(q => { (map[q.author] = map[q.author] || []).push({...q}); });
  const buckets = Object.values(map).sort((a,b) => b.length - a.length);
  const result = [];
  let safety = 0;
  while (result.length < arr.length && safety++ < arr.length * 4) {
    for (const b of buckets) {
      if (b.length && (result.length === 0 || result[result.length-1].author !== b[0].author)) {
        result.push(b.shift());
        if (result.length === arr.length) break;
      }
    }
  }
  buckets.forEach(b => b.forEach(q => { if (!result.find(r=>r.text===q.text)) result.push(q); }));
  return result;
}
const IDLE_QUOTES = interleaveByAuthor(IDLE_QUOTES_RAW);
const PHRASES = [
  "A distância entre o sonho e a realidade chama-se acção.",
  "Não contes os dias — faz os dias contar.",
  "O sucesso é a soma de pequenos esforços repetidos dia após dia.",
  "A motivação põe-te em movimento; o hábito mantém-te em marcha.",
  "Grandes resultados exigem grandes ambições.",
  "Cada cliente conquistado é um passo em direcção à excelência.",
  "Não há atalhos para qualquer lugar que valha a pena ir.",
  "Age como se o impossível fosse apenas uma questão de tempo.",
  "A persistência é o caminho do êxito.",
  "O teu trabalho de hoje é o teu legado de amanhã.",
  "Cada 'não' recebido é treino para o próximo 'sim'.",
  "Faz hoje o que os outros não querem, para amanhã teres o que os outros não têm.",
  "Disciplina é liberdade.",
  "Não esperes pela oportunidade certa — cria-a.",
  "O campeão não nasce campeão; torna-se campeão.",
  "Pequenas vitórias diárias constroem grandes conquistas.",
  "A excelência não é um destino, é uma prática contínua.",
  "Se não és tu, quem? Se não é agora, quando?",
  "O fracasso é apenas o pretexto para recomeçar com mais experiência.",
  "Vai mais longe do que ontem — sempre.",
  "O talento ganha jogos, mas o trabalho em equipa ganha campeonatos.",
  "Não há vento favorável para quem não sabe para onde vai.",
  "Conquista o teu dia antes que o dia te conquiste a ti.",
  "Cada chamada, cada visita, cada esforço — conta.",
  "A venda começa quando o cliente diz não.",
  "Sê tão bom que não te possam ignorar.",
  "Confia no processo — os resultados chegam para quem não desiste.",
  "Uma meta clara vale mais do que mil intenções vagas.",
  "O maior risco é não arriscar nada.",
  "Foca-te no progresso, não na perfeição.",
  "Os vencedores fazem o que os perdedores não querem fazer.",
  "O entusiasmo é a fagulha; a determinação é o fogo.",
  "Trabalha em silêncio — que o sucesso faça o ruído por ti.",
  "Cada dia é uma nova oportunidade de superar quem eras ontem.",
  "A grandeza não está na chegada, mas na jornada.",
  "O sucesso não é acidente; é trabalho duro, perseverança e sacrifício.",
  "Quem tem um porque suporta qualquer como.",
  "Os obstáculos são aquelas coisas assustadoras que vês quando tiras os olhos do objectivo.",
  "Cada meta alcançada é uma prova de que sonhar e agir funcionam juntos.",
  "A persistência vence o talento quando o talento não persiste.",
  "A equipa que celebra junta, vence junta.",
  "Faz do teu trabalho a tua obra de arte.",
  "O esforço de hoje é a confiança de amanhã.",
  "Não é o mais forte que sobrevive, mas o mais adaptável.",
  "Resultados extraordinários exigem acções extraordinárias.",
  "Cada dia sem progresso é um dia a favor da concorrência.",
  "O limite que enfrentas existe apenas na tua cabeça.",
  "A equipa certa supera sempre o indivíduo mais talentoso.",
  "Saber não é suficiente. Tens de aplicar.",
  "A ambição sem disciplina é apenas um sonho acordado.",
];
function parsePicture(s) {
  if (!s) return null;
  s = String(s);
  if (s.includes("|")) return s.split("|")[0];
  return s.replace(/(\.(jpg|jpeg|png|webp|gif)).*$/i, "$1");
}
function loadPhrasesEnabled() { const v=localStorage.getItem(PHRASES_SHOW_K); return v===null?true:v==="true"; }
function savePhrasesEnabled(v){ localStorage.setItem(PHRASES_SHOW_K,String(v)); }
function fmtDate(d) { if(!d) return "—"; return new Date(d).toLocaleDateString("pt-PT",{day:"2-digit",month:"2-digit",year:"numeric"}); }
function daysLeft(e)   { if(!e) return null; return Math.ceil((new Date(e)-new Date())/86400000); }
function pct(c,t)      { if(!t||t<=0) return 0; return Math.min(100,Math.round((c/t)*100)); }
function initials(n)   { return (n||"?").split(" ").slice(0,2).map(w=>w[0]).join("").toUpperCase(); }
function lighten(h,a=0.85){ const r=parseInt(h.slice(1,3),16),g=parseInt(h.slice(3,5),16),b=parseInt(h.slice(5,7),16),m=c=>Math.round(c+(255-c)*a); return `rgb(${m(r)},${m(g)},${m(b)})`; }
function darken(h,a=0.6)  { const r=parseInt(h.slice(1,3),16),g=parseInt(h.slice(3,5),16),b=parseInt(h.slice(5,7),16),m=c=>Math.round(c*(1-a)); return `rgb(${m(r)},${m(g)},${m(b)})`; }
function fmtTime()     { return new Date().toLocaleTimeString("pt-PT",{hour:"2-digit",minute:"2-digit",second:"2-digit"}); }
function fmtDateFull() { return new Date().toLocaleDateString("pt-PT",{weekday:"long",day:"2-digit",month:"long",year:"numeric"}); }
function getCountdown(e){
  if(!e) return null;
  const d=new Date(e).setHours(23,59,59,999)-Date.now();
  if(d<=0) return {days:0,hours:0,mins:0,secs:0,expired:true};
  return {days:Math.floor(d/86400000),hours:Math.floor((d%86400000)/3600000),mins:Math.floor((d%3600000)/60000),secs:Math.floor((d%60000)/1000),expired:false};
}
function urgencyLevel(g){
  const p=pct(g.currentCount,g.targetCount);
  if(p>=100) return "done";
  if(!g.endDate) return "nodate";
  const cd=getCountdown(g.endDate);
  if(!cd||cd.expired) return "critical";
  const dr=cd.days+cd.hours/24,prog=p/100;
  const td=g.startDate?Math.max(1,(new Date(g.endDate)-new Date(g.startDate))/86400000):30;
  const el=g.startDate?Math.max(0,(Date.now()-new Date(g.startDate))/86400000):td-dr;
  const lag=Math.min(1,el/td)-prog;
  if(dr<3&&p<80) return "critical";
  if(dr<7&&lag>0.25) return "critical";
  if(lag>0.3||dr<5) return "warning";
  return "ok";
}
const URGENCY_MSG={
  critical:" Pouco tempo para cumprir as metas!",
  warning:" Atenção — o ritmo actual pode não ser suficiente.",
  ok:" No caminho certo. Mantém o ritmo!",
  done:" Meta alcançada! Excelente trabalho!",
  nodate:" Sem prazo definido — foca-te no crescimento contínuo.",
};
const URGENCY_COLORS={critical:"#ef4444",warning:"#f59e0b",ok:"#10b981",done:"#6ee7b7",nodate:"#94a3b8"};
const zp=n=>String(n).padStart(2,"0");

const api = {
  list:     ()     => axios.get(Hoot()+"api/goals",{withCredentials:true}),
  create:   (body) => axios.post(Hoot()+"api/goals",body,{withCredentials:true}),
  update:   (id,b) => axios.put(Hoot()+`api/goals/${id}`,b,{withCredentials:true}),
  progress: (id,v) => axios.patch(Hoot()+`api/goals/${id}/progress`,{currentCount:v},{withCredentials:true}),
  pin:      (id,v) => axios.patch(Hoot()+`api/goals/${id}/pin`,{pinned:v},{withCredentials:true}),
  remove:   (id)   => axios.delete(Hoot()+`api/goals/${id}`,{withCredentials:true}),
};
const css = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,700&family=Inter:wght@400;500;600&display=swap');
.gt-fab-group{position:fixed;bottom:28px;right:28px;z-index:1300;display:flex;flex-direction:column;align-items:center;gap:10px;}
.gt-fab{width:52px;height:52px;border-radius:50%;background:var(--primary,#6052ff);color:#fff;border:none;box-shadow:0 6px 24px rgba(96,82,255,.38);display:grid;place-items:center;cursor:pointer;font-size:22px;transition:transform .12s,box-shadow .12s;}
.gt-fab:hover{transform:scale(1.08);box-shadow:0 10px 32px rgba(96,82,255,.48);}
.gt-fab:active{transform:scale(.97);}
.gt-fab-fs{width:44px;height:44px;border-radius:50%;background:var(--card,#fff);border:1px solid var(--border,#e2e8f0);box-shadow:0 4px 16px rgba(2,6,23,.12);display:grid;place-items:center;cursor:pointer;font-size:18px;color:var(--text,#0f172a);transition:transform .12s,background .12s;}
.gt-fab-fs:hover{transform:scale(1.08);background:var(--primary-soft,#ebe9ff);color:var(--primary,#6052ff);}
.gt-fab-fs.active-fs{background:var(--primary,#6052ff);color:#fff;border-color:var(--primary,#6052ff);animation:gt-pulse 2s infinite;}
@keyframes gt-pulse{0%,100%{box-shadow:0 6px 24px rgba(96,82,255,.5);}50%{box-shadow:0 6px 36px rgba(96,82,255,.85);}}
.gt-backdrop{position:fixed;inset:0;background:rgba(2,6,23,.52);z-index:1310;opacity:0;pointer-events:none;transition:opacity .18s;}
.gt-backdrop.open{opacity:1;pointer-events:auto;}
.gt-modal{position:fixed;top:0;right:0;height:100vh;width:min(780px,100vw);background:var(--card,#fff);border-left:1px solid var(--border,#e2e8f0);z-index:1320;transform:translateX(105%);transition:transform .22s;display:flex;flex-direction:column;box-shadow:-8px 0 48px rgba(2,6,23,.14);}
.gt-modal.open{transform:translateX(0);}
.gt-header{padding:16px 18px 14px;border-bottom:1px solid var(--border,#e2e8f0);display:flex;align-items:center;justify-content:space-between;gap:12px;flex-shrink:0;}
.gt-body{flex:1;overflow-y:auto;padding:16px 18px 24px;}
.gt-tabs{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:16px;}
.gt-tab{border-radius:999px;border:1px solid rgba(148,163,184,.35);background:transparent;padding:7px 14px;font-size:.82rem;font-weight:800;color:var(--muted,#6b7280);cursor:pointer;display:inline-flex;align-items:center;gap:6px;transition:background .12s,border-color .12s,color .12s;}
.gt-tab.active{background:var(--primary-soft,#ebe9ff);border-color:var(--primary-border,#d1ceff);color:var(--primary,#6052ff);}
.gt-user-row{display:grid;grid-template-columns:1fr;gap:12px;}
@media(min-width:520px){.gt-user-row{grid-template-columns:1fr 1fr;}}
.gt-user-card{border:1px solid var(--border,#e2e8f0);border-radius:18px;padding:14px;background:var(--card,#fff);display:flex;flex-direction:column;gap:10px;cursor:pointer;transition:transform .10s,border-color .12s;}
.gt-user-card:hover{transform:translateY(-1px);border-color:rgba(96,82,255,.35);}
.gt-user-card.selected{border-color:var(--primary,#6052ff);background:var(--primary-soft,#ebe9ff);}
.gt-prog-wrap{width:100%;border-radius:999px;background:rgba(148,163,184,.18);overflow:visible;position:relative;}
.gt-prog-fill{height:100%;border-radius:999px;transition:width .5s cubic-bezier(.4,0,.2,1);position:relative;overflow:visible;}
.gt-prog-avatar{position:absolute;right:-14px;top:50%;transform:translateY(-50%);width:28px;height:28px;border-radius:50%;border:2px solid #fff;overflow:hidden;background:rgba(148,163,184,.22);display:grid;place-items:center;font-size:.65rem;font-weight:900;z-index:2;flex-shrink:0;}
.gt-prog-avatar img{width:100%;height:100%;object-fit:cover;}
.gt-goal-card{border:1px solid var(--border,#e2e8f0);border-radius:18px;padding:14px;background:var(--card,#fff);margin-bottom:12px;}
.gt-goal-header{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;margin-bottom:10px;}
.gt-badge{display:inline-flex;align-items:center;gap:6px;border-radius:999px;padding:3px 10px;font-size:.72rem;font-weight:950;border:1px solid rgba(148,163,184,.25);}
.gt-form-card{border:1px solid var(--border,#e2e8f0);border-radius:18px;padding:14px;background:var(--card,#fff);margin-bottom:14px;}
.gt-label{font-size:.78rem;color:var(--muted,#6b7280);font-weight:950;margin-bottom:5px;}
.gt-input{width:100%;border:1px solid rgba(148,163,184,.35);border-radius:12px;padding:9px 12px;background:var(--card,#fff);color:var(--text,#0f172a);font-size:.88rem;font-weight:650;}
.gt-input:focus{outline:none;border-color:var(--primary,#6052ff);}
.gt-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
@media(max-width:460px){.gt-grid-2{grid-template-columns:1fr;}}
.gt-color-row{display:flex;flex-wrap:wrap;gap:8px;}
.gt-color-dot{width:26px;height:26px;border-radius:50%;cursor:pointer;border:3px solid transparent;transition:transform .08s,border-color .08s;}
.gt-color-dot:hover{transform:scale(1.12);}
.gt-color-dot.sel{border-color:#0f172a;}
.gt-btn{border-radius:999px;border:1px solid rgba(148,163,184,.35);background:var(--card,#fff);padding:8px 16px;font-size:.84rem;font-weight:850;cursor:pointer;display:inline-flex;align-items:center;gap:6px;transition:background .12s,border-color .12s;}
.gt-btn:hover{background:rgba(148,163,184,.10);}
.gt-btn.primary{background:var(--primary,#6052ff);color:#fff;border-color:var(--primary,#6052ff);}
.gt-btn.primary:hover{background:#4e42e0;}
.gt-btn.sm{padding:5px 12px;font-size:.78rem;}
.gt-icon-btn{width:34px;height:34px;border-radius:12px;border:1px solid rgba(148,163,184,.35);background:rgba(148,163,184,.08);display:grid;place-items:center;cursor:pointer;color:var(--text,#0f172a);transition:background .12s;}
.gt-icon-btn:hover{background:rgba(96,82,255,.10);border-color:rgba(96,82,255,.28);}
.gt-pinned-bar{position:fixed;top:0;left:0;right:0;z-index:1400;background:var(--card,#fff);border-bottom:1px solid var(--border,#e2e8f0);padding:6px 14px;display:flex;align-items:center;gap:14px;flex-wrap:nowrap;overflow-x:auto;scrollbar-width:none;box-shadow:0 2px 12px rgba(2,6,23,.08);}
.gt-pinned-bar::-webkit-scrollbar{display:none;}
.gt-pinned-item{display:flex;align-items:center;gap:8px;flex-shrink:0;min-width:180px;max-width:240px;}
.gt-pinned-prog{flex:1;height:8px;border-radius:999px;background:rgba(148,163,184,.18);overflow:hidden;}
.gt-pinned-fill{height:100%;border-radius:999px;transition:width .5s ease;}
.gt-pinned-label{font-size:.72rem;font-weight:900;white-space:nowrap;color:var(--text,#0f172a);}
.gt-no-goals{text-align:center;padding:32px 0;color:var(--muted,#6b7280);font-size:.86rem;font-weight:650;}
.gt-section-title{font-size:.72rem;font-weight:950;text-transform:uppercase;letter-spacing:.06em;color:var(--muted,#6b7280);margin-bottom:10px;}
.gt-fs-overlay{position:fixed;inset:0;z-index:9999;background:#060818;display:flex;flex-direction:column;overflow:hidden;opacity:0;pointer-events:none;transition:opacity .3s;}
.gt-fs-overlay.show{opacity:1;pointer-events:auto;}
.gt-fs-topbar{padding:14px 28px 0;display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
.gt-fs-title{font-size:1.3rem;font-weight:950;color:#fff;letter-spacing:.02em;display:flex;align-items:center;gap:10px;}
.gt-fs-clock{font-size:.82rem;color:rgba(255,255,255,.45);font-weight:600;font-variant-numeric:tabular-nums;}
.gt-fs-actions{display:flex;gap:8px;}
.gt-fs-btn{border-radius:10px;border:1px solid rgba(255,255,255,.15);background:rgba(255,255,255,.08);padding:7px 14px;font-size:.8rem;font-weight:850;cursor:pointer;display:inline-flex;align-items:center;gap:6px;color:#fff;transition:background .14s;}
.gt-fs-btn:hover{background:rgba(255,255,255,.16);}
.gt-fs-btn.exit{border-color:rgba(239,68,68,.4);color:#fca5a5;}
.gt-fs-btn.exit:hover{background:rgba(239,68,68,.12);}
.gt-fs-grid{flex:1;padding:12px 24px 8px;display:grid;gap:14px;overflow:hidden;min-height:0;}
.gt-fs-grid[data-count="1"]{grid-template-columns:1fr;max-width:680px;margin:0 auto;width:100%;}
.gt-fs-grid[data-count="2"]{grid-template-columns:1fr 1fr;}
.gt-fs-grid[data-count="3"]{grid-template-columns:1fr 1fr 1fr;}
.gt-fs-grid[data-count="4"]{grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;}
.gt-fs-grid[data-count="5"],.gt-fs-grid[data-count="6"]{grid-template-columns:repeat(3,1fr);grid-template-rows:1fr 1fr;}
.gt-fs-grid[data-count-many]{grid-template-columns:repeat(auto-fill,minmax(320px,1fr));}
.gt-fs-card{border-radius:20px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);padding:18px 20px 14px;display:flex;flex-direction:column;gap:10px;min-height:0;overflow:hidden;position:relative;transition:transform .15s,border-color .2s;}
.gt-fs-card:hover{transform:translateY(-2px);border-color:rgba(255,255,255,.14);}
.gt-fs-card.completed{border-color:rgba(16,185,129,.4);animation:gt-completed-pulse 3s ease infinite;}
.gt-fs-card.gt-rank-1{box-shadow:0 0 32px rgba(251,191,36,.2);}
@keyframes gt-completed-pulse{0%,100%{background:rgba(16,185,129,.04);}50%{background:rgba(16,185,129,.10);}}
.gt-fs-card-top{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;}
.gt-fs-card-name{font-size:1rem;font-weight:950;color:#fff;line-height:1.2;}
.gt-fs-card-sub{font-size:.78rem;color:rgba(255,255,255,.48);font-weight:650;margin-top:2px;}
.gt-fs-avatar{flex-shrink:0;border-radius:50%;overflow:hidden;display:grid;place-items:center;font-weight:950;border:2px solid rgba(255,255,255,.15);}
.gt-fs-avatar img{width:100%;height:100%;object-fit:cover;}
.gt-fs-prog-track{width:100%;border-radius:999px;background:rgba(255,255,255,.08);overflow:visible;position:relative;flex-shrink:0;}
.gt-fs-prog-fill{height:100%;border-radius:999px;position:relative;overflow:visible;transition:width .8s cubic-bezier(.4,0,.2,1);}
.gt-fs-prog-glow{position:absolute;inset:0;border-radius:999px;opacity:.4;filter:blur(7px);}
.gt-fs-prog-runner{position:absolute;top:50%;transform:translateY(-50%);border-radius:50%;border:3px solid #060818;overflow:hidden;display:grid;place-items:center;font-weight:950;z-index:2;}
.gt-fs-prog-runner img{width:100%;height:100%;object-fit:cover;}
.gt-fs-stats{display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;}
.gt-fs-pct{font-weight:950;line-height:1;letter-spacing:-.02em;}
.gt-fs-counts{text-align:right;}
.gt-fs-count-main{font-weight:950;color:#fff;}
.gt-fs-count-sub{color:rgba(255,255,255,.42);font-weight:650;}
.gt-cd-box{background:rgba(255,255,255,.05);border-radius:12px;padding:8px 10px;border:1px solid rgba(255,255,255,.07);flex-shrink:0;}
.gt-cd-row{display:flex;align-items:flex-end;gap:4px;justify-content:center;flex-wrap:wrap;}
.gt-cd-label{font-size:.55rem;color:rgba(255,255,255,.38);font-weight:700;letter-spacing:.04em;margin-bottom:1px;align-self:flex-end;}
.gt-cd-sep{color:rgba(255,255,255,.2);font-size:.9rem;font-weight:300;align-self:flex-end;margin-bottom:2px;line-height:1;}
.gt-cd-unit{text-align:center;}
.gt-cd-num{font-variant-numeric:tabular-nums;letter-spacing:-.01em;line-height:1;font-weight:950;}
.gt-cd-sub{font-size:.52rem;color:rgba(255,255,255,.3);font-weight:700;margin-top:1px;letter-spacing:.04em;}
.gt-fs-urgency{font-weight:950;text-align:center;letter-spacing:.01em;flex-shrink:0;}
.gt-fs-logo{position:absolute;bottom:8px;right:14px;font-size:.6rem;color:rgba(255,255,255,.16);font-weight:950;letter-spacing:.08em;text-transform:uppercase;}
.gt-fs-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;color:rgba(255,255,255,.35);gap:14px;}
.gt-fs-phrase-bar{flex-shrink:0;padding:7px 28px 12px;text-align:center;border-top:1px solid rgba(255,255,255,.05);transition:opacity .5s ease;}
.iq-overlay{position:fixed;inset:0;z-index:19999;display:flex;align-items:center;justify-content:center;overflow:hidden;pointer-events:none;opacity:0;transition:opacity .9s cubic-bezier(.4,0,.2,1);}
.iq-overlay.iq-visible{opacity:1;pointer-events:auto;}
.iq-bg{position:absolute;inset:0;transition:background 1.6s cubic-bezier(.4,0,.2,1);}
.iq-bg::after{content:'';position:absolute;inset:0;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");background-size:180px;pointer-events:none;opacity:.45;}
.iq-qmark-open{position:absolute;top:5%;left:4%;font-family:'Playfair Display',serif;font-size:clamp(72px,13vw,175px);line-height:1;font-weight:700;user-select:none;pointer-events:none;opacity:.18;letter-spacing:-.02em;transition:color 1.6s ease;}
.iq-qmark-close{position:absolute;bottom:9%;right:4%;font-family:'Playfair Display',serif;font-size:clamp(72px,13vw,175px);line-height:1;font-weight:700;user-select:none;pointer-events:none;opacity:.18;letter-spacing:-.02em;transition:color 1.6s ease;}
.iq-center{position:relative;z-index:2;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;max-width:min(840px,88vw);padding:0 24px;}
.iq-text{font-family:'Playfair Display',serif;font-size:clamp(1.4rem,3.4vw,2.85rem);font-weight:700;font-style:italic;line-height:1.3;letter-spacing:-.01em;color:#fff;opacity:0;transform:translateY(22px);transition:opacity .72s cubic-bezier(.4,0,.2,1),transform .72s cubic-bezier(.4,0,.2,1);text-shadow:0 2px 32px rgba(0,0,0,.2);}
.iq-text.iq-in{opacity:1;transform:translateY(0);}
.iq-divider{width:0;height:2px;border-radius:999px;margin:22px auto 18px;transition:width .62s cubic-bezier(.4,0,.2,1) .26s,background-color 1.6s ease;}
.iq-divider.iq-in{width:clamp(38px,7vw,70px);}
.iq-author-wrap{opacity:0;transform:translateY(12px);transition:opacity .55s cubic-bezier(.4,0,.2,1) .34s,transform .55s cubic-bezier(.4,0,.2,1) .34s;}
.iq-author-wrap.iq-in{opacity:1;transform:translateY(0);}
.iq-author{font-family:'Inter',sans-serif;font-size:clamp(.86rem,1.65vw,1.08rem);font-weight:600;letter-spacing:.05em;color:#fff;text-transform:uppercase;}
.iq-role{font-family:'Inter',sans-serif;font-size:clamp(.68rem,1.2vw,.84rem);font-weight:400;letter-spacing:.02em;margin-top:5px;color:rgba(255,255,255,.5);}
.iq-progress-bar{position:absolute;top:0;left:0;height:3px;border-radius:0 999px 999px 0;z-index:4;}
.iq-dots{position:absolute;bottom:46px;left:50%;transform:translateX(-50%);display:flex;gap:6px;z-index:3;flex-wrap:wrap;justify-content:center;max-width:80vw;}
.iq-dot{width:6px;height:6px;border-radius:50%;background:rgba(255,255,255,.2);transition:background .3s,transform .3s;flex-shrink:0;}
.iq-dot.iq-active{background:rgba(255,255,255,.85);transform:scale(1.3);}
.iq-bottom{position:absolute;bottom:0;left:0;right:0;padding:14px 24px;display:flex;align-items:center;justify-content:space-between;z-index:3;}
.iq-logo{font-family:'Inter',sans-serif;font-size:.74rem;font-weight:600;letter-spacing:.1em;color:rgba(255,255,255,.3);text-transform:uppercase;}
.iq-hint{font-family:'Inter',sans-serif;font-size:.68rem;font-weight:400;letter-spacing:.03em;color:rgba(255,255,255,.2);display:flex;align-items:center;gap:6px;}
.iq-hint-dot{width:6px;height:6px;border-radius:50%;background:rgba(255,255,255,.2);animation:iq-blink 2.2s ease infinite;}
@keyframes iq-blink{0%,100%{opacity:.3;}50%{opacity:1;}}
.gt-has-pinned .fixed-top{top:44px !important;}
.gt-has-pinned .app-main-wrapper{padding-top:100px !important;}
`;
function ProgressBar({ current, target, color, picUrl, name, height=12 }) {
  const p=pct(current,target), pic=parsePicture(picUrl);
  return (
    <div style={{position:"relative",marginBottom:8,paddingRight:20}}>
      <div className="gt-prog-wrap" style={{height}}>
        <div className="gt-prog-fill" style={{width:`${p}%`,background:color||"#6052ff",height}}>
          <div className="gt-prog-avatar" title={`${name}: ${p}%`}>
            {pic ? <img src={pic} alt={name} onError={e=>{e.currentTarget.style.display="none";}} /> : <span style={{color,fontSize:".6rem",fontWeight:900}}>{initials(name)}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function CountdownBlock({ endDate, color, size="md" }) {
  const [cd,setCd]=useState(()=>getCountdown(endDate));
  useEffect(()=>{ if(!endDate) return; const t=setInterval(()=>setCd(getCountdown(endDate)),1000); return ()=>clearInterval(t); },[endDate]);
  const ns=size==="lg"?"2rem":size==="sm"?".9rem":"1.3rem";
  const ds=size==="lg"?"2.6rem":size==="sm"?"1.1rem":"1.7rem";
  if(!endDate) return <div className="gt-cd-box" style={{textAlign:"center"}}><span style={{fontSize:".76rem",color:"rgba(255,255,255,.3)",fontWeight:650}}>Sem prazo definido</span></div>;
  if(!cd||cd.expired) return <div className="gt-cd-box" style={{textAlign:"center"}}><span style={{fontSize:".8rem",color:"#ef4444",fontWeight:950}}>Prazo expirado</span></div>;
  const uc=cd.days<3?"#ef4444":cd.days<7?"#f59e0b":color||"#fff";
  return (
    <div className="gt-cd-box">
      <div className="gt-cd-row">
        <span className="gt-cd-label">Faltam</span>
        {[{val:cd.days,label:"dias"},{val:cd.hours,label:"h"},{val:cd.mins,label:"min"},{val:cd.secs,label:"seg"}].map((it,i)=>(
          <React.Fragment key={i}>
            {i>0 && <span className="gt-cd-sep">:</span>}
            <div className="gt-cd-unit">
              <div className="gt-cd-num" style={{fontSize:it.label==="dias"?ds:ns,color:uc}}>{it.label==="dias"?cd.days:zp(it.val)}</div>
              <div className="gt-cd-sub">{it.label}</div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
function FullscreenCard({ goal:g, rank, totalCards }) {
  const p=pct(g.currentCount,g.targetCount), pic=parsePicture(g.userPicture), done=p>=100, urg=urgencyLevel(g);
  const rc=["#fbbf24","#94a3b8","#cd7c2e"];
  const re=["🥇","🥈","🥉"];
  const sm=totalCards>=5, md=totalCards===3||totalCards===4;
  const pctSz=sm?"2rem":md?"2.6rem":"3.2rem", barH=sm?14:md?18:22, runSz=sm?28:md?36:44, avSz=sm?40:md?48:56, cdSz=sm?"sm":md?"md":"lg";
  return (
    <div className={`gt-fs-card ${done?"completed":""} ${rank===1?"gt-rank-1":""}`}
      style={{borderColor:urg==="critical"?"rgba(239,68,68,.4)":urg==="warning"?"rgba(245,158,11,.28)":done?"rgba(16,185,129,.4)":"rgba(255,255,255,.08)"}}>
      <div style={{position:"absolute",top:0,left:0,right:0,height:4,borderRadius:"20px 20px 0 0",background:g.color,boxShadow:`0 0 20px ${g.color}99`}}/>
      {rank<=3 && <div style={{position:"absolute",top:10,left:12,fontSize:sm?"1rem":"1.25rem",filter:"drop-shadow(0 2px 6px rgba(0,0,0,.5))"}}>{re[rank-1]}</div>}
      <div className="gt-fs-card-top" style={{paddingLeft:rank<=3?(sm?26:32):0}}>
        <div style={{flex:1,minWidth:0}}>
          <div className="gt-fs-card-name" style={{fontSize:sm?".9rem":"1rem"}}>{g.label}</div>
          <div className="gt-fs-card-sub" style={{display:"flex",alignItems:"center",gap:6}}>
            {g.userName}
            {rank<=3 && <span style={{fontSize:".65rem",fontWeight:950,padding:"2px 7px",borderRadius:999,border:`1px solid ${rc[rank-1]}55`,background:`${rc[rank-1]}18`,color:rc[rank-1]}}>#{rank}</span>}
          </div>
        </div>
        <div className="gt-fs-avatar" style={{width:avSz,height:avSz,background:lighten(g.color,.12),borderColor:`${g.color}55`}}>
          {pic ? <img src={pic} alt={g.userName} onError={e=>{e.currentTarget.style.display="none";}} /> : <span style={{color:g.color,fontSize:sm?".85rem":"1rem",fontWeight:950}}>{initials(g.userName)}</span>}
        </div>
      </div>
      <div className="gt-fs-stats">
        <div className="gt-fs-pct" style={{fontSize:pctSz,color:done?"#6ee7b7":g.color}}>{p}%</div>
        <div className="gt-fs-counts">
          <div className="gt-fs-count-main" style={{fontSize:sm?".9rem":"1.05rem"}}>{g.currentCount} / {g.targetCount}</div>
          <div className="gt-fs-count-sub" style={{fontSize:sm?".68rem":".76rem"}}>{g.startDate?`${fmtDate(g.startDate)} → ${fmtDate(g.endDate)}`:"Sem prazo"}</div>
        </div>
      </div>
      <div style={{paddingRight:runSz/2+6,flexShrink:0}}>
        <div className="gt-fs-prog-track" style={{height:barH}}>
          <div className="gt-fs-prog-glow" style={{width:`${p}%`,background:g.color,height:barH}}/>
          <div className="gt-fs-prog-fill" style={{width:`${p}%`,background:`linear-gradient(90deg,${darken(g.color,.15)},${g.color})`,height:barH}}>
            <div className="gt-fs-prog-runner" style={{width:runSz,height:runSz,right:-(runSz/2),background:g.color,fontSize:runSz>34?".75rem":".62rem"}}>
              {pic ? <img src={pic} alt={g.userName} onError={e=>{e.currentTarget.style.display="none";}} /> : <span style={{color:"#fff",fontWeight:950}}>{initials(g.userName)}</span>}
            </div>
          </div>
        </div>
      </div>
      <CountdownBlock endDate={g.endDate} color={g.color} size={cdSz} />
      <div className="gt-fs-urgency" style={{fontSize:sm?".68rem":".74rem",color:URGENCY_COLORS[urg]||"rgba(255,255,255,.45)"}}>{URGENCY_MSG[urg]}</div>
      <div className="gt-fs-logo">Eduall CRM</div>
    </div>
  );
}

function FullscreenOverlay({ goals, pinned, onClose }) {
  const [clock,setClock]=useState(fmtTime());
  const [date,setDate]=useState(fmtDateFull());
  const [phraseIdx,setPhraseIdx]=useState(()=>Math.floor(Math.random()*PHRASES.length));
  const [phraseVis,setPhraseVis]=useState(true);
  const fsRef=useRef(null);
  const [nativeFs,setNativeFs]=useState(false);
  const sorted=useMemo(()=>{
    const raw=pinned.map(id=>goals.find(g=>g.id===id)).filter(Boolean);
    return [...raw].sort((a,b)=>pct(b.currentCount,b.targetCount)-pct(a.currentCount,a.targetCount));
  },[pinned,goals]);
  useEffect(()=>{const t=setInterval(()=>{setClock(fmtTime());setDate(fmtDateFull());},1000);return()=>clearInterval(t);},[]);
  useEffect(()=>{
    const t=setInterval(()=>{setPhraseVis(false);setTimeout(()=>{setPhraseIdx(i=>(i+1)%PHRASES.length);setPhraseVis(true);},550);},12000);
    return()=>clearInterval(t);
  },[]);
  const toggleNativeFs=()=>{
    if(!document.fullscreenElement){fsRef.current?.requestFullscreen?.().catch(()=>{});setNativeFs(true);}
    else{document.exitFullscreen?.();setNativeFs(false);}
  };
  useEffect(()=>{const h=()=>{if(!document.fullscreenElement)setNativeFs(false);};document.addEventListener("fullscreenchange",h);return()=>document.removeEventListener("fullscreenchange",h);},[]);
  useEffect(()=>{const h=e=>{if(e.key==="Escape"&&!document.fullscreenElement)onClose();};window.addEventListener("keydown",h);return()=>window.removeEventListener("keydown",h);},[onClose]);
  const overallUrgency=useMemo(()=>{
    if(!sorted.length) return "nodate";
    const order=["critical","warning","ok","done","nodate"];
    return sorted.map(g=>urgencyLevel(g)).sort((a,b)=>order.indexOf(a)-order.indexOf(b))[0];
  },[sorted]);
  const count=sorted.length, gridAttr=count>6?{"data-count-many":""}:{"data-count":count};
  return (
    <div className="gt-fs-overlay show" ref={fsRef} style={{display:"flex",flexDirection:"column"}}>
      <div className="gt-fs-topbar">
        <div className="gt-fs-title">
          <FiTarget style={{color:"#6052ff",fontSize:22}}/>
          Metas da Equipa
          <span className="gt-fs-clock">{date} &middot; {clock}</span>
        </div>
        <div className="gt-fs-actions">
          <button className="gt-fs-btn" onClick={toggleNativeFs}>
            {nativeFs?<FiMinimize style={{fontSize:14}}/>:<FiMaximize style={{fontSize:14}}/>}
            {nativeFs?"Sair ecrã":"Ecrã completo"}
          </button>
          <button className="gt-fs-btn exit" onClick={onClose}><FiX style={{fontSize:14}}/> Fechar</button>
        </div>
      </div>
      {count>0 && overallUrgency!=="done" && (
        <div style={{margin:"5px 24px 0",padding:"5px 16px",borderRadius:10,flexShrink:0,
          background:overallUrgency==="critical"?"rgba(239,68,68,.12)":overallUrgency==="warning"?"rgba(245,158,11,.10)":"rgba(16,185,129,.08)",
          border:`1px solid ${overallUrgency==="critical"?"rgba(239,68,68,.28)":overallUrgency==="warning"?"rgba(245,158,11,.22)":"rgba(16,185,129,.2)"}`,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
          <span style={{fontSize:".8rem",fontWeight:950,color:URGENCY_COLORS[overallUrgency]}}>{URGENCY_MSG[overallUrgency]}</span>
        </div>
      )}
      {count===0 ? (
        <div className="gt-fs-empty" style={{flex:1}}>
          <FiTarget style={{fontSize:48,opacity:.3}}/>
          <div style={{fontSize:"1.1rem",fontWeight:950}}>Sem metas afixadas</div>
          <div style={{fontSize:".88rem"}}>Afixe metas no gestor para as ver aqui.</div>
        </div>
      ) : (
        <div className="gt-fs-grid" {...gridAttr} style={{flex:1,minHeight:0}}>
          {sorted.map((g,i)=><FullscreenCard key={g.id} goal={g} rank={i+1} totalCards={count}/>)}
        </div>
      )}
      <div className="gt-fs-phrase-bar" style={{opacity:phraseVis?1:0}}>
        <span style={{fontStyle:"italic",fontSize:".8rem",fontWeight:650,letterSpacing:".02em",color:"rgba(255,255,255,.3)"}}>
          &ldquo;{PHRASES[phraseIdx]}&rdquo;
        </span>
      </div>
    </div>
  );
}

function IdleQuoteScreen({ enabled }) {
  const [visible,setVisible]=useState(false);
  const [qIdx,setQIdx]=useState(0);
  const [inAnim,setInAnim]=useState(false);
  const [progress,setProgress]=useState(0);
  const idleTimer=useRef(null), rotateTimer=useRef(null);
  const startProgress=useCallback(()=>{setProgress(0);requestAnimationFrame(()=>requestAnimationFrame(()=>setProgress(100)));}, []);
  const nextQuote=useCallback(()=>{setInAnim(false);setTimeout(()=>{setQIdx(i=>(i+1)%IDLE_QUOTES.length);setInAnim(true);startProgress();},550);},[startProgress]);
  const showIdle=useCallback(()=>{setQIdx(Math.floor(Math.random()*IDLE_QUOTES.length));setVisible(true);setTimeout(()=>{setInAnim(true);startProgress();},80);rotateTimer.current=setInterval(nextQuote,IDLE_ROTATE_MS);},[nextQuote,startProgress]);
  const hideIdle=useCallback(()=>{setVisible(false);setInAnim(false);clearInterval(rotateTimer.current);rotateTimer.current=null;},[]);
  const resetIdle=useCallback(()=>{if(visible) hideIdle();clearTimeout(idleTimer.current);if(enabled) idleTimer.current=setTimeout(showIdle,IDLE_MS);},[visible,hideIdle,showIdle,enabled]);
  useEffect(()=>{
    const EVTS=["mousemove","mousedown","keydown","touchstart","scroll","click","wheel"];
    EVTS.forEach(ev=>window.addEventListener(ev,resetIdle,{passive:true}));
    if(enabled) idleTimer.current=setTimeout(showIdle,IDLE_MS);
    return()=>{EVTS.forEach(ev=>window.removeEventListener(ev,resetIdle));clearTimeout(idleTimer.current);clearInterval(rotateTimer.current);};
  },[resetIdle,showIdle,enabled]);
  useEffect(()=>{ if(!enabled && visible) hideIdle(); },[enabled,visible,hideIdle]);
  const q=IDLE_QUOTES[qIdx];
  return (
    <div className={`iq-overlay ${visible?"iq-visible":""}`} onClick={resetIdle}>
      <div className="iq-bg" style={{background:q.gradient}}/>
      <div className="iq-progress-bar" style={{width:`${progress}%`,background:q.accent,opacity:.72,transition:progress===0?"none":`width ${IDLE_ROTATE_MS/1000}s linear`}}/>
      <div className="iq-qmark-open" style={{color:q.accent}}>&ldquo;</div>
      <div className="iq-qmark-close" style={{color:q.accent}}>&rdquo;</div>
      <div className="iq-center">
        <div className={`iq-text ${inAnim?"iq-in":""}`}>{q.text}</div>
        <div className={`iq-divider ${inAnim?"iq-in":""}`} style={{background:q.accent}}/>
        <div className={`iq-author-wrap ${inAnim?"iq-in":""}`}>
          <div className="iq-author" style={{color:q.accent}}>{q.author}</div>
          <div className="iq-role">{q.role}</div>
        </div>
      </div>
      <div className="iq-dots">{IDLE_QUOTES.map((_,i)=><div key={i} className={`iq-dot ${i===qIdx?"iq-active":""}`}/>)}</div>
      <div className="iq-bottom">
        <span className="iq-logo">Eduall CRM</span>
        <span className="iq-hint"><span className="iq-hint-dot"/>Mover o rato para voltar</span>
      </div>
    </div>
  );
}
export default function GoalsTracker() {
  const [open,setOpen]=useState(false);
  const [fsOpen,setFsOpen]=useState(false);
  const [tab,setTab]=useState("overview");
  const [users,setUsers]=useState([]);
  const [loadingUsers,setLoadingUsers]=useState(false);
  const [loadingGoals,setLoadingGoals]=useState(false);
  const [goals,setGoals]=useState([]);
  const [pinned,setPinned]=useState([]);
  const [phrasesEnabled,setPhrasesEnabled]=useState(loadPhrasesEnabled);
  const [selUser,setSelUser]=useState(null);
  const [editingGoal,setEditingGoal]=useState(null);
  const [form,setForm]=useState({label:"",targetCount:"",currentCount:"0",startDate:"",endDate:"",color:PALETTE[0],note:""});

  useEffect(()=>{ fetchUsers(); fetchGoals(); },[]);
  useEffect(()=>{ savePhrasesEnabled(phrasesEnabled); },[phrasesEnabled]);
  useEffect(()=>{
    if(pinned.length>0) document.documentElement.classList.add("gt-has-pinned");
    else document.documentElement.classList.remove("gt-has-pinned");
    return()=>document.documentElement.classList.remove("gt-has-pinned");
  },[pinned.length]);

  const fetchUsers=async()=>{
    try{ setLoadingUsers(true); const r=await axios.get(Hoot()+"api/manager-accounts",{withCredentials:true}); setUsers(r.data||[]); }
    catch{} finally{setLoadingUsers(false);}
  };

  const fetchGoals=async()=>{
    try{
      setLoadingGoals(true);
      const r=await api.list();
      const rows=r.data?.data||[];
      const data=rows.map(g=>({
        id:g.id, userId:g.userId||g.goal_user_id,
        userName:g.userName||g.goal_user_name,
        userEmail:g.userEmail||g.goal_user_email,
        userPicture:g.userPicture||g.goal_user_pic,
        label:g.label||g.goal_label,
        targetCount:Number(g.targetCount||g.goal_target)||1,
        currentCount:Number(g.currentCount||g.goal_current)||0,
        color:g.color||g.goal_color||PALETTE[0],
        note:g.note||g.goal_note||"",
        startDate:g.startDate||g.goal_start_date||"",
        endDate:g.endDate||g.goal_end_date||"",
        createdAt:g.createdAt||g.goal_created_at,
        updatedAt:g.updatedAt||g.goal_updated_at,
      }));
      setGoals(data);
      const pinnedIds=rows.filter(g=>g.pinned||g.goal_pinned).map(g=>g.id);
      setPinned(pinnedIds);
    }catch{ toast.error("Erro ao carregar metas."); }
    finally{setLoadingGoals(false);}
  };

  const resetForm=()=>{ setForm({label:"",targetCount:"",currentCount:"0",startDate:"",endDate:"",color:PALETTE[0],note:""}); setSelUser(null); setEditingGoal(null); };
  const openNew=()=>{ resetForm(); setTab("new"); };
  const openEdit=g=>{ setEditingGoal(g); setSelUser(users.find(u=>u.id===g.userId)||null); setForm({label:g.label||"",targetCount:String(g.targetCount||""),currentCount:String(g.currentCount||"0"),startDate:g.startDate||"",endDate:g.endDate||"",color:g.color||PALETTE[0],note:g.note||""}); setTab("new"); };

  const saveGoal=async()=>{
    if(!selUser)           return toast.warning("Selecione um utilizador.");
    if(!form.label.trim()) return toast.warning("Defina um título para a meta.");
    if(!form.targetCount||isNaN(Number(form.targetCount))||Number(form.targetCount)<=0) return toast.warning("Target inválido.");
    const payload={userId:selUser.id,userName:selUser.name,userEmail:selUser.email,userPicture:selUser.picture,label:form.label.trim(),targetCount:Number(form.targetCount),currentCount:Number(form.currentCount)||0,startDate:form.startDate||null,endDate:form.endDate||null,color:form.color,note:form.note||null,pinned:editingGoal?pinned.includes(editingGoal.id):false};
    try{
      if(editingGoal){
        await api.update(editingGoal.id,payload);
        setGoals(prev=>prev.map(g=>g.id===editingGoal.id?{...g,...payload,updatedAt:new Date().toISOString()}:g));
        toast.success("Meta atualizada!");
      } else {
        const r=await api.create(payload);
        const newId=r.data?.data?.id||r.data?.id||`local_${Date.now()}`;
        setGoals(prev=>[...prev,{...payload,id:newId,createdAt:new Date().toISOString(),updatedAt:new Date().toISOString()}]);
        toast.success("Meta criada!");
      }
    }catch{ toast.error("Erro ao guardar meta."); }
    resetForm(); setTab("overview");
  };

  const deleteGoal=async id=>{ try{ await api.remove(id); }catch{} setGoals(p=>p.filter(g=>g.id!==id)); setPinned(p=>p.filter(x=>x!==id)); toast.success("Meta removida."); };
  const updateCurrent=async(id,val)=>{ const v=Math.max(0,Number(val)||0); setGoals(p=>p.map(g=>g.id===id?{...g,currentCount:v,updatedAt:new Date().toISOString()}:g)); try{ await api.progress(id,v); }catch{} };
  const togglePin=async id=>{ const np=!pinned.includes(id); setPinned(p=>np?[...p,id]:p.filter(x=>x!==id)); try{ await api.pin(id,np); }catch{} };

  const pinnedGoals=useMemo(()=>pinned.map(id=>goals.find(g=>g.id===id)).filter(Boolean),[pinned,goals]);
  const goalsByUser=useMemo(()=>{ const m={}; goals.forEach(g=>{ if(!m[g.userId])m[g.userId]={userName:g.userName,userPicture:g.userPicture,goals:[]}; m[g.userId].goals.push(g); }); return m; },[goals]);

  const PinnedBar=()=>{
    if(!pinnedGoals.length) return null;
    return (
      <div className="gt-pinned-bar">
        <span style={{fontSize:".72rem",fontWeight:950,color:"var(--muted)",whiteSpace:"nowrap",flexShrink:0}}><FiTarget style={{marginRight:4,verticalAlign:-2}}/> Metas</span>
        {pinnedGoals.map(g=>{ const p=pct(g.currentCount,g.targetCount),pic=parsePicture(g.userPicture); return (
          <div className="gt-pinned-item" key={g.id}>
            <div style={{width:24,height:24,borderRadius:"50%",overflow:"hidden",background:lighten(g.color,.7),display:"grid",placeItems:"center",flexShrink:0,fontSize:".6rem",fontWeight:900,color:g.color,border:`1.5px solid ${g.color}`}}>
              {pic?<img src={pic} alt={g.userName} style={{width:"100%",height:"100%",objectFit:"cover"}} onError={e=>{e.currentTarget.style.display="none";}}/>:initials(g.userName)}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div className="gt-pinned-label" style={{overflow:"hidden",textOverflow:"ellipsis"}}>{g.label}</div>
              <div className="gt-pinned-prog"><div className="gt-pinned-fill" style={{width:`${p}%`,background:g.color}}/></div>
            </div>
            <span style={{fontSize:".72rem",fontWeight:950,color:g.color,flexShrink:0}}>{p}%</span>
          </div>
        );})}
        <div style={{display:"flex",gap:6,marginLeft:"auto",flexShrink:0}}>
          <button className="gt-icon-btn" title="Modo visualização" onClick={()=>setFsOpen(true)}><FiMonitor style={{fontSize:14}}/></button>
          <button className="gt-icon-btn" title="Gerir metas" onClick={()=>setOpen(true)}><FiEdit2 style={{fontSize:14}}/></button>
        </div>
      </div>
    );
  };

  const GoalCard=({goal:g})=>{
    const [editing,setEditing]=useState(false);
    const [localCurrent,setLocalCurrent]=useState(String(g.currentCount));
    const p=pct(g.currentCount,g.targetCount),dl=daysLeft(g.endDate),isPinned=pinned.includes(g.id);
    const commit=()=>{ updateCurrent(g.id,localCurrent); setEditing(false); };
    return (
      <div className="gt-goal-card" style={{borderLeft:`4px solid ${g.color}`}}>
        <div className="gt-goal-header">
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:950,fontSize:".92rem",marginBottom:3}}>{g.label}</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {g.startDate && <span className="gt-badge" style={{background:lighten(g.color,.88),color:g.color,borderColor:lighten(g.color,.6)}}><FiCalendar style={{fontSize:11}}/> {fmtDate(g.startDate)} → {fmtDate(g.endDate)}</span>}
              {dl!==null && <span className="gt-badge" style={{background:dl<7?"#fee2e2":dl<30?"#fef3c7":"#dbeafe",color:dl<7?"#991b1b":dl<30?"#92400e":"#1d4ed8",borderColor:"transparent"}}>{dl>0?`${dl}d restantes`:dl===0?"Hoje!":"Expirada"}</span>}
            </div>
          </div>
          <div style={{display:"flex",gap:6,flexShrink:0}}>
            <Tooltip title={isPinned?"Desafixar do topo":"Afixar no topo"} arrow>
              <button className="gt-icon-btn" onClick={()=>togglePin(g.id)} style={isPinned?{background:lighten(g.color,.8),borderColor:g.color}:{}}>
                {isPinned?<FiEye style={{fontSize:14,color:g.color}}/>:<FiEyeOff style={{fontSize:14}}/>}
              </button>
            </Tooltip>
            <Tooltip title="Editar" arrow><button className="gt-icon-btn" onClick={()=>openEdit(g)}><FiEdit2 style={{fontSize:14}}/></button></Tooltip>
            <Tooltip title="Remover" arrow><button className="gt-icon-btn" onClick={()=>deleteGoal(g.id)} style={{borderColor:"rgba(239,68,68,.3)"}}><FiTrash2 style={{fontSize:14,color:"#ef4444"}}/></button></Tooltip>
          </div>
        </div>
        <ProgressBar current={g.currentCount} target={g.targetCount} color={g.color} picUrl={g.userPicture} name={g.userName} height={14}/>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {editing ? (
              <>
                <input className="gt-input" type="number" min="0" style={{width:90}} value={localCurrent} onChange={e=>setLocalCurrent(e.target.value)} onKeyDown={e=>e.key==="Enter"&&commit()} autoFocus/>
                <button className="gt-btn sm primary" onClick={commit}><FiSave/> OK</button>
                <button className="gt-btn sm" onClick={()=>{setLocalCurrent(String(g.currentCount));setEditing(false);}}>Cancelar</button>
              </>
            ) : (
              <>
                <span style={{fontSize:".88rem",fontWeight:950,color:g.color}}>{g.currentCount} / {g.targetCount}</span>
                <button className="gt-btn sm" onClick={()=>{setLocalCurrent(String(g.currentCount));setEditing(true);}}><FiEdit2 style={{fontSize:12}}/> Actualizar</button>
              </>
            )}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:42,height:42,borderRadius:"50%",background:lighten(g.color,.88),border:`2px solid ${g.color}`,display:"grid",placeItems:"center",fontSize:".82rem",fontWeight:950,color:g.color}}>{p}%</div>
            {g.note && <span style={{fontSize:".76rem",color:"var(--muted)",fontStyle:"italic",maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{g.note}</span>}
          </div>
        </div>
      </div>
    );
  };

  const Overview=()=>(
    <>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:10,flexWrap:"wrap"}}>
        <div>
          <div style={{fontWeight:950,fontSize:"1rem"}}>Visão geral das metas</div>
          <div style={{fontSize:".8rem",color:"var(--muted)",fontWeight:650}}>{goals.length} meta(s){loadingGoals?" · A carregar…":""}</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          {pinnedGoals.length>0 && <Tooltip title="Abrir modo visualização" arrow><button className="gt-btn" onClick={()=>{setOpen(false);setFsOpen(true);}}><FiMonitor style={{fontSize:15}}/> Visualizar</button></Tooltip>}
          <button className="gt-btn primary" onClick={openNew}><FiPlus/> Nova meta</button>
        </div>
      </div>

      {/* MUI Switch — frases motivacionais */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderRadius:14,background:"rgba(148,163,184,.08)",border:"1px solid rgba(148,163,184,.2)",marginBottom:14}}>
        <div>
          <div style={{fontSize:".84rem",fontWeight:750,color:"var(--text,#0f172a)"}}>Frases motivacionais</div>
          <div style={{fontSize:".74rem",color:"var(--muted,#6b7280)",marginTop:2}}>Após {IDLE_MS/60000} min sem actividade</div>
        </div>
        <Switch
          checked={phrasesEnabled}
          onChange={(_,v)=>setPhrasesEnabled(v)}
          sx={{"& .MuiSwitch-switchBase.Mui-checked":{"color":"#6052ff"},"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":{"backgroundColor":"#6052ff"}}}
        />
      </div>

      {goals.length===0 ? (
        <div className="gt-no-goals"><FiTarget style={{fontSize:32,opacity:.4,display:"block",margin:"0 auto 10px"}}/>Nenhuma meta criada ainda.<br/>Clique em <b>Nova meta</b> para começar.</div>
      ) : (
        Object.entries(goalsByUser).map(([uid,data])=>(
          <div key={uid} style={{marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
              <Avatar src={parsePicture(data.userPicture)||undefined} sx={{width:32,height:32,fontSize:".78rem",fontWeight:900}}>{initials(data.userName)}</Avatar>
              <div style={{fontWeight:950,fontSize:".9rem"}}>{data.userName}</div>
              <span style={{fontSize:".72rem",color:"var(--muted)",fontWeight:650}}>{data.goals.length} meta(s)</span>
            </div>
            {data.goals.map(g=><GoalCard key={g.id} goal={g}/>)}
          </div>
        ))
      )}
    </>
  );

  const GoalForm=()=>(
    <>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
        <button className="gt-btn sm" onClick={()=>{resetForm();setTab("overview");}}>&#8592; Voltar</button>
        <span style={{fontWeight:950,fontSize:".95rem"}}>{editingGoal?"Editar meta":"Nova meta"}</span>
      </div>
      <div className="gt-form-card">
        <div className="gt-section-title"><FiUsers style={{verticalAlign:-2}}/> Selecionar utilizador</div>
        {loadingUsers ? <div style={{fontSize:".82rem",color:"var(--muted)"}}>A carregar utilizadores…</div> : (
          <div className="gt-user-row">
            {users.map(u=>(
              <div key={u.id} className={`gt-user-card ${selUser?.id===u.id?"selected":""}`} onClick={()=>setSelUser(u)}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <Avatar src={parsePicture(u.picture)||undefined} sx={{width:38,height:38,fontSize:".8rem",fontWeight:900}}>{initials(u.name)}</Avatar>
                  <div><div style={{fontWeight:950,fontSize:".86rem"}}>{u.name}</div><div style={{fontSize:".74rem",color:"var(--muted)"}}>{u.charge||u.email}</div></div>
                </div>
              </div>
            ))}
            {users.length===0 && <div style={{fontSize:".82rem",color:"var(--muted)"}}>Nenhum utilizador.<button className="gt-btn sm" style={{marginLeft:8}} onClick={fetchUsers}><FiRefreshCw style={{fontSize:13}}/> Recarregar</button></div>}
          </div>
        )}
      </div>
      <div className="gt-form-card">
        <div className="gt-section-title"><FiAward style={{verticalAlign:-2}}/> Detalhes da meta</div>
        <div style={{marginBottom:10}}><div className="gt-label">Título *</div><input className="gt-input" placeholder="Ex.: Clientes angariados em Maio" value={form.label} onChange={e=>setForm(p=>({...p,label:e.target.value}))}/></div>
        <div className="gt-grid-2" style={{marginBottom:10}}>
          <div><div className="gt-label">Target *</div><input className="gt-input" type="number" min="1" placeholder="Ex.: 30" value={form.targetCount} onChange={e=>setForm(p=>({...p,targetCount:e.target.value}))}/></div>
          <div><div className="gt-label">Progresso actual</div><input className="gt-input" type="number" min="0" placeholder="0" value={form.currentCount} onChange={e=>setForm(p=>({...p,currentCount:e.target.value}))}/></div>
        </div>
        <div className="gt-grid-2" style={{marginBottom:10}}>
          <div><div className="gt-label">Data início</div><input className="gt-input" type="date" value={form.startDate} onChange={e=>setForm(p=>({...p,startDate:e.target.value}))}/></div>
          <div><div className="gt-label">Data fim</div><input className="gt-input" type="date" value={form.endDate} onChange={e=>setForm(p=>({...p,endDate:e.target.value}))}/></div>
        </div>
        <div style={{marginBottom:10}}><div className="gt-label">Nota</div><textarea className="gt-input" rows={2} placeholder="Contexto, observações…" value={form.note} onChange={e=>setForm(p=>({...p,note:e.target.value}))} style={{resize:"vertical"}}/></div>
        <div><div className="gt-label">Cor da meta</div><div className="gt-color-row">{PALETTE.map(c=><div key={c} className={`gt-color-dot ${form.color===c?"sel":""}`} style={{background:c,borderColor:form.color===c?"#0f172a":"transparent"}} onClick={()=>setForm(p=>({...p,color:c}))}/>)}</div></div>
      </div>
      {selUser && form.targetCount && (
        <div className="gt-form-card">
          <div className="gt-section-title"><FiTrendingUp style={{verticalAlign:-2}}/> Pré-visualização</div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <Avatar src={parsePicture(selUser.picture)||undefined} sx={{width:30,height:30,fontSize:".72rem"}}>{initials(selUser.name)}</Avatar>
            <span style={{fontWeight:950,fontSize:".86rem"}}>{selUser.name}</span>
            <span style={{fontSize:".78rem",color:"var(--muted)"}}>&#8212; {form.label||"Meta"}</span>
          </div>
          <ProgressBar current={Number(form.currentCount)||0} target={Number(form.targetCount)||1} color={form.color} picUrl={selUser.picture} name={selUser.name} height={14}/>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
            <span style={{fontSize:".78rem",color:"var(--muted)"}}>{Number(form.currentCount)||0} / {Number(form.targetCount)||0}</span>
            <span style={{fontSize:".78rem",fontWeight:950,color:form.color}}>{pct(Number(form.currentCount)||0,Number(form.targetCount)||1)}%</span>
          </div>
        </div>
      )}
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button className="gt-btn" onClick={()=>{resetForm();setTab("overview");}}>Cancelar</button>
        <button className="gt-btn primary" onClick={saveGoal}><FiSave/> {editingGoal?"Guardar alterações":"Criar meta"}</button>
      </div>
    </>
  );

  return (
    <>
      <style>{css}</style>
      <IdleQuoteScreen enabled={phrasesEnabled}/>
      <PinnedBar/>
      {fsOpen && <FullscreenOverlay goals={goals} pinned={pinned} onClose={()=>setFsOpen(false)}/>}

      <div className="gt-fab-group">
        <Tooltip title={pinnedGoals.length>0?"Modo visualização (TV/Monitor)":"Afixe metas para activar"} arrow placement="left">
          <button className={`gt-fab-fs ${fsOpen?"active-fs":""}`}
            onClick={()=>pinnedGoals.length>0?setFsOpen(true):toast.info("Afixe pelo menos uma meta para usar este modo.")}
            aria-label="Modo visualização"><FiMonitor/></button>
        </Tooltip>
        <Tooltip title="Gestor de metas" arrow placement="left">
          <button className="gt-fab" onClick={()=>setOpen(true)} aria-label="Abrir gestor de metas"><FiTarget/></button>
        </Tooltip>
      </div>

      <div className={`gt-backdrop ${open?"open":""}`} onClick={()=>{setOpen(false);resetForm();setTab("overview");}}/>

      <div className={`gt-modal ${open?"open":""}`} aria-modal="true" role="dialog">
        <div className="gt-header">
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:38,height:38,borderRadius:12,background:"var(--primary-soft,#ebe9ff)",border:"1px solid var(--primary-border,#d1ceff)",display:"grid",placeItems:"center",color:"var(--primary,#6052ff)"}}><FiTarget style={{fontSize:18}}/></div>
            <div>
              <div style={{fontWeight:950,fontSize:"1rem"}}>Gestor de Metas</div>
              <div style={{fontSize:".78rem",color:"var(--muted)",fontWeight:650}}>{goals.length} meta(s) &middot; {pinned.length} afixada(s)</div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {pinnedGoals.length>0 && <Tooltip title="Abrir modo visualização" arrow><button className="gt-icon-btn" onClick={()=>{setOpen(false);setFsOpen(true);}}><FiMonitor style={{fontSize:14}}/></button></Tooltip>}
            <Tooltip title="Recarregar" arrow>
              <button className="gt-icon-btn" onClick={()=>{fetchUsers();fetchGoals();}} disabled={loadingUsers||loadingGoals}><FiRefreshCw style={{fontSize:14}}/></button>
            </Tooltip>
            <button className="gt-icon-btn" onClick={()=>{setOpen(false);resetForm();setTab("overview");}} aria-label="Fechar"><FiX style={{fontSize:16}}/></button>
          </div>
        </div>

        <div className="gt-body">
          <div className="gt-tabs">
            <button className={`gt-tab ${tab==="overview"?"active":""}`} onClick={()=>setTab("overview")}><FiTrendingUp/> Visão geral</button>
            <button className={`gt-tab ${tab==="new"?"active":""}`} onClick={openNew}><FiPlus/> {editingGoal?"Editar meta":"Nova meta"}</button>
            {pinnedGoals.length>0 && <button className={`gt-tab ${tab==="pinned"?"active":""}`} onClick={()=>setTab("pinned")}><FiEye/> Afixadas ({pinnedGoals.length})</button>}
          </div>

          {tab==="overview" && <Overview/>}
          {tab==="new"      && <GoalForm/>}
          {tab==="pinned"   && (
            <>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,gap:10}}>
                <div>
                  <div style={{fontWeight:950,fontSize:".95rem"}}>Metas afixadas no topo</div>
                  <div style={{fontSize:".8rem",color:"var(--muted)",fontWeight:650}}>Visíveis acima do navbar e no modo visualização.</div>
                </div>
                <button className="gt-btn" onClick={()=>{setOpen(false);setFsOpen(true);}}><FiMonitor style={{fontSize:14}}/> Visualizar</button>
              </div>
              {pinnedGoals.length===0 ? (
                <div className="gt-no-goals">Nenhuma meta afixada.<br/>Clique no ícone <FiEye style={{verticalAlign:-2}}/> numa meta para a afixar.</div>
              ) : (
                pinnedGoals.map(g=>{ const p=pct(g.currentCount,g.targetCount),pic=parsePicture(g.userPicture); return (
                  <div key={g.id} className="gt-goal-card" style={{borderLeft:`4px solid ${g.color}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                      <Avatar src={pic||undefined} sx={{width:32,height:32,fontSize:".72rem"}}>{initials(g.userName)}</Avatar>
                      <div style={{flex:1}}><div style={{fontWeight:950,fontSize:".9rem"}}>{g.label}</div><div style={{fontSize:".76rem",color:"var(--muted)"}}>{g.userName}</div></div>
                      <span style={{fontWeight:950,fontSize:".92rem",color:g.color}}>{p}%</span>
                      <Tooltip title="Desafixar" arrow><button className="gt-icon-btn" onClick={()=>togglePin(g.id)}><FiEyeOff style={{fontSize:14}}/></button></Tooltip>
                    </div>
                    <ProgressBar current={g.currentCount} target={g.targetCount} color={g.color} picUrl={g.userPicture} name={g.userName} height={10}/>
                    <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
                      <span style={{fontSize:".76rem",color:"var(--muted)"}}>{g.currentCount} / {g.targetCount}{g.endDate&&` · até ${fmtDate(g.endDate)}`}</span>
                    </div>
                  </div>
                );})
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}